import React, { Component, Fragment } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { observer } from 'mobx-react'
import ReactResizeDetector from 'react-resize-detector'
import * as R from 'ramda'
import queryString from 'query-string'
import i18n from 'i18next'
import DynamicDashboardStore from './DynamicDashboardStore'
import WidgetContainer from './WidgetContainer'
import NoDashboardsView from './NoDashboardsView'
import AppLoading from '../AppLoading'
import TemplateCloneModal from './TemplateCloneModal'
import ManageDashboardsModal from './ManageDashboardsModal'
import Button from '../../UniformComponents/Button'
import LevelSelector from './LevelSelector'
import CreateDashboardWizard from './CreateDashboardModal'
import SelectTemplateModal from './SelectTemplateModal'
import CreateTemplateModal from './CreateTemplateModal'
import DashboardHeader from './DashboardHeader'

import './dynamicDashboard.scss'
import '../../../node_modules/react-grid-layout/css/styles.css'
import DashboardClone from './modal/DashboardClone'
import DashboardRename from './modal/DashboardRename'
import DashboardDelete from './modal/DashboardDelete'
import DashboardExport from './modal/DashboardExport'
import ConfirmationModal from '../../UniformComponents/ConfirmationModal'
import ComparisonModal from './ComparisonModal'
import LockSlider from './LockSlider'
import WidgetCreatorStore from './creationWizard/WidgetCreatorV3/WidgetCreatorStore'

import FilterModal from '../ReportV2/components/Modal/Filter'
import FilterRespondentTypeModal from './FilterRespondentTypeModal'
import Select from '../form/Select'
import AddPinnedFilterModal from './AddPinnedFilterModal'
import AddPinnedCustomTagFilterModal from './AddPinnedCustomTagFilterModal'
import ThreeDotMenu from './ThreeDotMenu'
import TemplateDuplicateModal from './TemplateDuplicateModal'
import WidgetCreatorNew from './creationWizard/WidgetCreatorV3/WidgetCreator'

const ResponsiveGridLayout = WidthProvider(Responsive)
@observer
class DynamicDashboard extends Component {
    constructor(props) {
        super(props)
        this.state = {
            initializing: true,
            showComparisonModal: false,
            showSaveConfirmationModal: false,
        }
        this.init()
    }

    componentDidMount() {
        const { store, surveyDash } = this.props
        if (!surveyDash) {
            store.breadcrumps = [
                {
                    name: 'dashboard',
                    url: '/company/dashboard',
                    title: i18n.t('dashboard'),
                },
            ]
        }
        window.addEventListener('beforeunload', this.handleBeforeUnload)
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
    }

    init = async () => {
        const { store } = this.props
        await this.setInitialRoute()
        this.DynamicDashboardStore = new DynamicDashboardStore(store)
        this.DynamicDashboardStore.init()
        this.widgetCreatorStore = new WidgetCreatorStore(this.DynamicDashboardStore)
        await this.widgetCreatorStore.init()
        this.setState({ initializing: false })
    }

    handleBeforeUnload = (event) => {
        const { editing } = this.DynamicDashboardStore
        if (editing) {
            const message = 'You have unsaved changes. Are you sure you want to leave?'
            event.returnValue = message
            return message
        }
    };

    setInitialRoute = async () => {
        const { location } = window
        const { store, history } = this.props
        const { userData } = store
        const { role } = userData
        const params = queryString.parse(location.search)

        if ([100, 110, 150].includes(role)) return

        const [divisions, portfolios, products] = await Promise.all(
            [
                this.getDivisions(),
                this.getPortfolios(),
                this.getProducts(),
            ],
        )

        const divisionIds = divisions.map((d) => String(d.divisionId))
        const portfolioIds = portfolios.map((p) => String(p.portfolioId))
        const productIds = products.map((p) => String(p.productId))

        const recordId = params.productId || params.portfolioId || params.divisionId
        if (!recordId) {
            if (role === 120) {
                history.push(`?divisionId=${divisions[0].divisionId}`)
            } else if (role === 130) {
                history.push(`?portfolioId=${portfolios[0].portfolioId}`)
            } else if ([140, 170].includes(role)) {
                history.push(`?productId=${products[0].productId}`)
            }
        } else if (params.productId && !productIds.includes(params.productId)) {
            history.push(`?productId=${products[0].productId}`)
        } else if (params.portfolioId && !portfolioIds.includes(params.portfolioId)) {
            history.push(`?portfolioId=${portfolios[0].portfolioId}`)
        } else if (params.divisionId && !divisionIds.includes(params.divisionId)) {
            history.push(`?divisionId=${divisions[0].divisionId}`)
        }
    }

    getDivisions = async () => {
        const { store } = this.props
        const { userData } = store
        const { role } = userData
        if (role > 120 && role !== 150) return []
        try {
            return await store.http.get('/division')
        } catch (e) {
            return []
        }
    }

    getPortfolios = async () => {
        const { store } = this.props
        const { userData } = store
        const { role } = userData
        if (role > 130 && role !== 150) return []
        try {
            return await store.http.get('/portfolio')
        } catch (e) {
            return []
        }
    }

    getProducts = async () => {
        const { store } = this.props
        try {
            return await store.http.get('/product')
        } catch (e) {
            return []
        }
    }

    onElementResize = (layout, oldItem, newItem, placeholder, e, element) => {
        this.DynamicDashboardStore.resizingElement = newItem.i
    }

    onElementResizeStop = () => {
        this.DynamicDashboardStore.resizingElement = undefined
    }

    renderElements = () => {
        const {
            widgets, getChart, removeChart, years, benchmarks, benchmarkPeriod,
        } = this.DynamicDashboardStore
        const chartProps = {
            hideMetadata: true,
            style: {
                marginBottom: 0,
                paddingBottom: 0,
                height: '100%',
                width: '100%',
            },
        }
        // reading the years here triggers mobx to rerender correctly.
        // won't rerender when read in the getChart function only
        const yearFilters = Array.from(years.keys()).filter((y) => !!years.get(y))

        const benchmarkFilters = Array.from(benchmarks.keys()).filter((y) => !!benchmarks.get(y))

        const elements = widgets.map((el) => {
            chartProps.chartId = `elChartId ${el.key}`
            chartProps.title = this.getChartTitle(el)
            chartProps.kpi = el.kpiType
            const scrollable = R.pathOr(false, ['widgetRenderSettings', 'scrollable'], el)
            return (
                <div
                    key={`${el.key}`}
                    style={{ width: '100%' }}
                >
                    <ReactResizeDetector>
                        {({ width, height }) => (
                            <WidgetContainer
                                chart={getChart(
                                    { ...el, containerSize: { width, height } },
                                    el.data,
                                    yearFilters, benchmarkFilters, benchmarkPeriod,
                                )}
                                title={el.title}
                                renderChart={width}
                                removeChart={removeChart}
                                chartKey={el.key}
                                DynamicDashboardStore={this.DynamicDashboardStore}
                                scrollable={scrollable}
                                chartType={el.chartType}
                                widget={el}
                                toggleV3WidgetEdit={this.widgetCreatorStore.toggleV3WidgetEdit}
                            />
                        )}
                    </ReactResizeDetector>
                </div>

            )
        })
        return elements
    }

    renderLevelHeaderButtons =() => {
        const { history, surveyDash } = this.props
        const { toggleManageDashboardsModal, toggleShowCreateDashboard, appStore } = this.DynamicDashboardStore
        const { userData } = appStore
        const { role } = userData
        if ([170, 150].includes(role)) return null
        return (
            <div className="headerLevelButtons">
                <Button
                    label={i18n.t('gen_manage')}
                    icon="fa-light fa-sliders"
                    cssClass="large tertiary"
                    onClick={toggleManageDashboardsModal}
                />
                <Button
                    label={i18n.t('dashboards.createDashboards')}
                    icon="fa-regular fa-plus"
                    cssClass="large tertiary"
                    onClick={toggleShowCreateDashboard}
                />
                {surveyDash && (
                    <Button
                        onClick={() => history.push('/company/dashboard/')}
                        icon="fa-light fa-gauge-simple-min"
                        cssClass="large secondary"
                        label={i18n.t('dashboards.allCompany')}
                    />
                )}
            </div>
        )
    }

    renderLevelSelect = () => {
        const {
            changeLevel, getLevels, level, recordId,
        } = this.DynamicDashboardStore
        const levels = getLevels()

        return (
            <LevelSelector
                levels={Array.from(levels).map(([l, dashboard]) => ({ level: l, dashboard }))}
                currentLevel={level}
                changeLevel={changeLevel}
                key={`levelSelector_${recordId}`}
            />
        )
    }

    toggleComparisonModal = () => {
        const { showComparisonModal } = this.state
        this.setState({ showComparisonModal: !showComparisonModal })
    }

    renderMenu = () => (
        <ThreeDotMenu
            menuOptions={this.DynamicDashboardStore.getMenuOptions()}
        />
    )

    pinnedFilter = () => {
        const {
            filterStore, editPinnedFilter, getPinnedFilterValue,
        } = this.DynamicDashboardStore
        const { pinnedFilterOptions, pinnedFilterQuestion } = filterStore
        const filterValue = getPinnedFilterValue()
        if (!pinnedFilterQuestion) return null

        return (
            <div className="pinnedFilter" style={{ width: '250px' }}>
                <Select
                    defaultValue={filterValue}
                    options={pinnedFilterOptions.map((o) => ({ ...o, value: o.id }))}
                    label={i18n.t('report.filter.quick.enable')}
                    onBlur={editPinnedFilter}
                    isMulti
                    blurOnClear
                    isClearable
                    showNumberSelected
                    key={pinnedFilterQuestion}
                />
            </div>
        )
    }

    dashboardPinnedFilter = () => {
        const {
            editDashboardFilterOptions, dashboardFilters, filterQuestions, openEnded, init, removeDashboardFilters,
            updateFilterChangeKey,
            editing,
        } = this.DynamicDashboardStore

        return dashboardFilters.map((filter, i) => {
            const options = openEnded.get(filter.question)
            || filterQuestions.find((q) => q.id === filter.question)?.options
            || []

            const filterValue = filter.filterType === 'multi'
                ? filter.options?.map((o) => o.value) || []
                : filter.options?.[0]?.value
            return (
                <div
                    className="pinnedFilter"
                    style={{
                        width: '250px', display: 'flex', alignItems: 'center', gap: 8,
                    }}
                >
                    <Select
                        defaultValue={filterValue}
                        options={options.map((o) => ({ ...o, value: o.id || o.surveyDataIds.join('_') }))}
                        label={filter.filterLabel}
                        onBlur={(value) => editDashboardFilterOptions(value, i, filter.filterType)}
                        isMulti={filter.filterType === 'multi'}
                        blurOnClear
                        showNumberSelected={filter.filterType === 'multi'}
                        key={filter.question}
                        onMenuClose={() => {
                            updateFilterChangeKey()
                            init(true)
                        }}
                        style={{ width: '100%' }}
                        isClearable
                    />
                    {editing
                        ? (
                            <ThreeDotMenu
                                menuOptions={(
                                    <li
                                        onClick={() => removeDashboardFilters(i)}
                                    >
                                        <i className="far fa-xmark" />
                                        {i18n.t('gen_remove_filter')}
                                    </li>
                                )}
                                key={uuidv4()}
                            />
                        )
                        : null }
                </div>
            )
        })
    }

    dashboardPinnedCustomTagFilter = () => {
        const {
            editPinnedCustomTagfilter, pinnedCustomTagFilters, init, benchmarkTags, removeDashboardCustomTagFilters,
            editing,
        } = this.DynamicDashboardStore

        return pinnedCustomTagFilters.map((filter, i) => {
            const tagOptions = R.pathOr(
                [], ['tagOptions'], benchmarkTags.find((t) => t.customTagId === filter.customTag),
            )

            if (!tagOptions) return null
            const options = tagOptions
                .map((top) => ({ value: top.tagOptionId, label: top.tagOptionName }))
                .sort((a, b) => a.value - b.value)

            const filterValue = filter.filterType === 'multi'
                ? R.or(filter.options, [])
                : filter.options?.[0]

            return (
                <div
                    className="pinnedFilter"
                    style={{
                        width: '250px', display: 'flex', alignItems: 'center', gap: 8,
                    }}
                >
                    <Select
                        defaultValue={filterValue}
                        options={options}
                        label={filter.filterLabel}
                        onBlur={
                            ({ value }) => (filter.filterType === 'multi'
                                ? editPinnedCustomTagfilter(value.map((v) => v.value), i)
                                : editPinnedCustomTagfilter([value], i))
                        }
                        isMulti={filter.filterType === 'multi'}
                        blurOnClear
                        showNumberSelected={filter.filterType === 'multi'}
                        key={filter.key}
                        onMenuClose={() => init(true)}
                        isClearable
                    />
                    {editing
                        ? (
                            <ThreeDotMenu
                                menuOptions={(
                                    <li
                                        onClick={() => removeDashboardCustomTagFilters(i)}
                                    >
                                        <i className="far fa-xmark" />
                                        {i18n.t('gen_remove_filter')}
                                    </li>
                                )}
                                key={uuidv4()}
                            />
                        ) : null }
                </div>
            )
        })
    }

    proceedToSaveDashboard = (shouldSave, cancelAction = false) => {
        const { beginSaveDashboard } = this.DynamicDashboardStore
        if (shouldSave) beginSaveDashboard()
        this.DynamicDashboardStore.editDashboard(cancelAction)
        this.setState({ showSaveConfirmationModal: false })
    }

    toggleShowSaveConfirmationModal = (val) => {
        const { showSaveConfirmationModal } = this.state
        this.setState({ showSaveConfirmationModal: val ?? !showSaveConfirmationModal })
    }

    renderDashboardMenuOptions = () => {
        const {
            editing,
            editDashboard,
            dashboard,
            appStore,
            loading,
        } = this.DynamicDashboardStore
        // const { toggleShow } = this.dashboardWizardStore
        const { showSaveConfirmationModal } = this.state

        const { userData } = appStore

        const canEdit = ![150, 170].includes(userData.role) && !loading

        return (
            <Fragment>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '16px',
                }}
                >
                    {dashboard && R.pathOr(undefined, ['value'], dashboard) !== 'new' && canEdit
                        ? (
                            <Fragment>
                                <b>{i18n.t('dashboards.editDashboard')}</b>
                                <LockSlider
                                    save={this.toggleShowSaveConfirmationModal}
                                    edit={editDashboard}
                                    editing={editing}
                                />
                            </Fragment>
                        )
                        : null}
                    {' '}
                </div>
                {dashboard && R.pathOr(undefined, ['value'], dashboard) !== 'new'
                    ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Fragment>
                                {!loading
                                    ? (
                                        <Button
                                            cssClass="large tertiary"
                                            onClick={this.toggleComparisonModal}
                                            icon="fa-light fa-filter-list"
                                            label={i18n.t('dashboards.addComparison')}
                                        />
                                    ) : null}
                                {canEdit ? (
                                    <Fragment>
                                        {process.env.NEW_WIDGET_CREATOR && appStore.isExploriAdmin() && editing
                                            ? (
                                                <Button
                                                    cssClass="large tertiary"
                                                    onClick={this.widgetCreatorStore.toggleShowWidgetCreatorV3}
                                                    icon="fa-light fa-chart-pie-simple"
                                                    label={`${i18n.t('dashboards.createWidget')} - v3`}
                                                />
                                            )
                                            : null }
                                        {process.env.USE_NEW_DASHBOARD_ENDPOINT && this.DynamicDashboardStore.oId === 6 && appStore.isExploriAdmin() && editing
                                            ? (
                                                <Button
                                                    cssClass="large tertiary"
                                                    onClick={this.DynamicDashboardStore.toggleShowAddPinnedFilters}
                                                    icon="fa-light fa-filter"
                                                    label={i18n.t('report.filter.add')}
                                                />
                                            ) : null }
                                        {process.env.USE_NEW_DASHBOARD_ENDPOINT && this.DynamicDashboardStore.oId !== 6 && appStore.isExploriAdmin() && editing
                                            ? (
                                                <Button
                                                    cssClass="large tertiary"
                                                    onClick={this.DynamicDashboardStore.toggleShowAddPinnedCustomTagFilters}
                                                    icon="fa-light fa-chart-pie-simple"
                                                    label={i18n.t('dashboard.addPinnedCustomTagFilter')}
                                                />
                                            ) : null }
                                        {this.renderMenu()}
                                    </Fragment>
                                ) : null }
                            </Fragment>
                        </div>
                    )
                    : null}
                <ConfirmationModal
                    show={showSaveConfirmationModal}
                    title={i18n.t('dashboards.modals.saveConfirmation.title')}
                    description={i18n.t('dashboards.modals.saveConfirmation.desc')}
                    onClose={() => this.proceedToSaveDashboard(false, true)}
                    onFalse={() => this.proceedToSaveDashboard(false)}
                    onFalseLabel={i18n.t('no')}
                    onFalseCss="error"
                    onTrue={() => this.proceedToSaveDashboard(true)}
                />
            </Fragment>
        )
    }

    getChartTitle = (chart) => chart.title

    render() {
        const { surveyDash, history, store } = this.props
        const { survey } = store
        const { showBranchBanner } = survey
        const { initializing } = this.state
        if (initializing) return <AppLoading noBg />
        const {
            layouts,
            editing,
            defaultGridProps,
            responsiveCols,
            gapSize,
            widgets,
            loading,
            showManageDashboardsModal,
            toggleManageDashboardsModal,
            confirmationModal,
            dashboard,
            scopeName,
        } = this.DynamicDashboardStore

        const {
            show, onTrue, onFalse, onClose, title, description,
        } = confirmationModal

        const layoutClone = Object.keys(layouts).reduce((newLayout, l) => (
            { ...newLayout, [l]: layouts[l].slice() }
        ), {})

        if (loading) {
            return (
                <div>
                    {!surveyDash && (
                        <DashboardHeader
                            DynamicDashboardStore={this.DynamicDashboardStore}
                            history={history}
                        />
                    )}
                    <div className={`${showBranchBanner ? 'branching' : ''} ${surveyDash ? 'survey-view-wrapper' : ''}`} style={{ padding: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                minHeight: 'calc(100vh - 350px)',
                            }}
                            className="grid-parent"
                        >
                            <div className="levelHeader" style={{ position: 'relative', height: 72, justifyContent: 'flex-end' }}>
                                {/* dont render levels while loading */}
                                {/* {this.renderLevelSelect()} */}
                                {this.renderLevelHeaderButtons()}
                            </div>
                            <div className="menuOptionsHeader">
                                {this.renderDashboardMenuOptions()}
                            </div>
                            <div style={{ width: '100%' }} className="grid-Container">
                                <div style={{
                                    height: '200px',
                                    position: 'relative',
                                }}
                                >
                                    <AppLoading noBg showTypewriter />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <Fragment>
                <div>
                    {!surveyDash && (
                        <DashboardHeader
                            DynamicDashboardStore={this.DynamicDashboardStore}
                            history={history}
                        />
                    )}
                </div>

                <div id="dynamicDashboard" className={`${showBranchBanner ? 'branching' : ''} ${surveyDash ? 'survey-view-wrapper' : ''}`}>
                    <h2 style={{ display: 'none' }} id="dashboardTitle">
                        {dashboard.name}
                        <span style={{ fontSize: '22px', fontWeight: 600 }}>{surveyDash ? ` - ${scopeName}` : ''}</span>
                    </h2>
                    {widgets && widgets.length
                        ? (
                            <div
                                style={{
                                    display: 'flex', alignItems: 'center', flexDirection: 'column',
                                }}
                                className="grid-parent"
                            >
                                <div className="levelHeader" style={{ position: 'relative' }}>
                                    {this.renderLevelSelect()}
                                    {this.renderLevelHeaderButtons()}
                                </div>
                                <div className="menuOptionsHeader">
                                    {this.renderDashboardMenuOptions()}
                                </div>
                                <div className="pinnedFilter" style={{ paddingLeft: '24px', width: '100%' }}>
                                    {this.pinnedFilter()}
                                </div>
                                <div
                                    className="pinnedFilter"
                                    style={{
                                        padding: '8px 24px', width: '100%', display: 'flex', gap: '16px',
                                    }}
                                >
                                    {this.dashboardPinnedFilter()}
                                    {this.dashboardPinnedCustomTagFilter()}
                                </div>
                                <div style={{ width: '100%' }} className="grid-Container" id="dashboardContainer">
                                    <ResponsiveGridLayout
                                        theStore={this.DynamicDashboardStore}
                                        className="layout"
                                        layouts={layoutClone}
                                        breakpoints={{
                                            lg: 1400, md: 1109, sm: 768, xs: 480, xxs: 380,
                                        }}
                                        margin={[gapSize, gapSize]}
                                        cols={responsiveCols}
                                        {...defaultGridProps}
                                        onLayoutChange={this.DynamicDashboardStore.onLayoutChange}
                                        isDraggable={editing}
                                        isResizable={editing}
                                        onResizeStart={this.onElementResize}
                                        onResizeStop={this.onElementResizeStop}
                                    >
                                        {this.renderElements()}
                                    </ResponsiveGridLayout>
                                </div>
                            </div>
                        )
                        : (
                            <div
                                style={{
                                    display: 'flex', alignItems: 'center', flexDirection: 'column',
                                }}
                                className="grid-parent"
                            >
                                <div className="levelHeader" style={{ position: 'relative' }}>
                                    {this.renderLevelSelect()}
                                    {this.renderLevelHeaderButtons()}
                                </div>
                                <div className="menuOptionsHeader">
                                    {this.renderDashboardMenuOptions()}
                                </div>
                                <NoDashboardsView
                                    dashStore={this.DynamicDashboardStore}
                                    showCreateWidget={this.widgetCreatorStore.toggleShowWidgetCreatorV3}
                                />
                            </div>
                        )}
                    <CreateDashboardWizard
                        dashStore={this.DynamicDashboardStore}
                    />
                    <TemplateCloneModal
                        show={this.DynamicDashboardStore.showTemplateCloneModal}
                        dashStore={this.DynamicDashboardStore}
                        onClose={this.DynamicDashboardStore.hideTemplateCloneModal}
                    />
                    <TemplateDuplicateModal
                        show={this.DynamicDashboardStore.showDuplicateModal}
                        dashStore={this.DynamicDashboardStore}
                        onClose={this.DynamicDashboardStore.hideDuplicateModal}
                    />
                    <DashboardClone
                        show={this.DynamicDashboardStore.showDashboardClone}
                        dashStore={this.DynamicDashboardStore}
                        onClose={() => { this.DynamicDashboardStore.showDashboardClone = false }}
                        key={uuidv4()}
                    />
                    <DashboardRename
                        show={this.DynamicDashboardStore.showDashboardRename}
                        dashStore={this.DynamicDashboardStore}
                        onClose={() => { this.DynamicDashboardStore.showDashboardRename = false }}
                        key={uuidv4()}
                    />
                    <DashboardDelete
                        show={this.DynamicDashboardStore.showDashboardDelete}
                        dashStore={this.DynamicDashboardStore}
                        onClose={() => { this.DynamicDashboardStore.showDashboardDelete = false }}
                    />
                    <ComparisonModal
                        dashStore={this.DynamicDashboardStore}
                        showComparisonModal={this.state.showComparisonModal}
                        toggleComparisonModal={this.toggleComparisonModal}
                    />
                    <FilterModal
                        show={this.DynamicDashboardStore.showFilters}
                        reportStore={this.DynamicDashboardStore.filterStore}
                        filters={this.DynamicDashboardStore.filters}
                        onSave={this.DynamicDashboardStore.saveFilter}
                        onClose={() => { this.DynamicDashboardStore.showFilters = false }}
                        hideCampaignFilters
                        hideDateFilters
                    />
                </div>
                <DashboardExport
                    show={this.DynamicDashboardStore.showDashboardExport}
                    dashStore={this.DynamicDashboardStore}
                    onClose={() => { this.DynamicDashboardStore.showDashboardExport = false }}
                    key={uuidv4()}
                />
                {showManageDashboardsModal
                    ? (
                        <ManageDashboardsModal
                            dashStore={this.DynamicDashboardStore}
                            showManageDashboardsModal
                            onClose={toggleManageDashboardsModal}
                        />
                    )
                    : null }
                <ConfirmationModal
                    show={show}
                    title={title}
                    description={description}
                    onClose={onClose}
                    onTrue={onTrue}
                    onFalse={onFalse}
                />
                {process.env.NEW_WIDGET_CREATOR ? (
                    <>
                        <WidgetCreatorNew
                            widgetCreatorStore={this.widgetCreatorStore}
                            widgetKey={this.DynamicDashboardStore.widgetEditorKeyNew}
                        />
                    </>
                ) : null }
                <FilterRespondentTypeModal
                    dashStore={this.DynamicDashboardStore}
                />
                <AddPinnedFilterModal
                    dashStore={this.DynamicDashboardStore}
                    key={uuidv4()}
                />
                <AddPinnedCustomTagFilterModal
                    dashStore={this.DynamicDashboardStore}
                    key={uuidv4()}
                />
                <SelectTemplateModal
                    dashStore={this.DynamicDashboardStore}
                />
                <CreateTemplateModal
                    dashStore={this.DynamicDashboardStore}
                />
            </Fragment>
        )
    }
}

export default DynamicDashboard
